import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Eye, 
  EyeOff, 
  Copy, 
  RefreshCw, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  X,
  Hash,
  AlertTriangle,
  CheckCircle,
  Code,
  Shield,
  Clock,
  Globe,
  Layers,
  Download
} from 'lucide-react';

interface UuidSettings {
  version: 'v1' | 'v4' | 'v7' | 'v6' | 'nil';
  format: 'standard' | 'noDashes' | 'uppercase' | 'lowercase' | 'braces' | 'urn';
  quantity: number;
  includeTimestamp: boolean;
  namespace: string;
  name: string;
}

interface UuidAnalytics {
  totalGenerated: number;
  averagePerGeneration: number;
  mostCommonVersion: string;
  versionDistribution: {
    v1: number;
    v4: number;
    v7: number;
    v6: number;
    nil: number;
  };
  formatDistribution: {
    standard: number;
    noDashes: number;
    uppercase: number;
    lowercase: number;
    braces: number;
    urn: number;
  };
  generationHistory: Array<{
    timestamp: Date;
    version: string;
    format: string;
    quantity: number;
  }>;
}

type UuidQuality = 'basic' | 'standard' | 'professional' | 'enterprise';

const UuidGenerator: React.FC = () => {
  const [uuids, setUuids] = useState<string[]>(['']);
  const [showUuids, setShowUuids] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [uuidQuality, setUuidQuality] = useState<UuidQuality>('standard');

  const [settings, setSettings] = useState<UuidSettings>({
    version: 'v4',
    format: 'standard',
    quantity: 1,
    includeTimestamp: false,
    namespace: '6ba7b810-9dad-11d1-80b4-00c04fd430c8',
    name: 'example'
  });

  const [analytics, setAnalytics] = useState<UuidAnalytics>({
    totalGenerated: 0,
    averagePerGeneration: 0,
    mostCommonVersion: 'v4',
    versionDistribution: { v1: 0, v4: 0, v7: 0, v6: 0, nil: 0 },
    formatDistribution: { standard: 0, noDashes: 0, uppercase: 0, lowercase: 0, braces: 0, urn: 0 },
    generationHistory: []
  });

  // UUID generation functions
  const generateV4 = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const generateV1 = (): string => {
    const time = Date.now() * 10000 + 122192928000000000; // Convert to UUID epoch
    const timeHex = time.toString(16).padStart(16, '0');
    const timeLow = timeHex.slice(-8);
    const timeMid = timeHex.slice(-12, -8);
    const timeHigh = (parseInt(timeHex.slice(-16, -12), 16) | 0x1000).toString(16);
    
    const clockSeq = Math.floor(Math.random() * 16384) | 0x8000;
    const node = Array.from({length: 6}, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join('');
    
    return `${timeLow}-${timeMid}-${timeHigh}-${clockSeq.toString(16)}-${node}`;
  };

  const generateV7 = (): string => {
    const timestamp = Date.now();
    const timestampHex = timestamp.toString(16).padStart(12, '0');
    const randomA = Math.floor(Math.random() * 4096) | 0x7000;
    const randomB = Math.floor(Math.random() * 65536) | 0x8000;
    const randomC = Array.from({length: 12}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    
    return `${timestampHex.slice(0, 8)}-${timestampHex.slice(8)}-${randomA.toString(16)}-${randomB.toString(16)}-${randomC}`;
  };

  const generateV6 = (): string => {
    // UUID v6 is a reordered timestamp-based UUID
    const time = Date.now() * 10000 + 122192928000000000;
    const timeHex = time.toString(16).padStart(16, '0');
    const timeHigh = timeHex.slice(0, 8);
    const timeMid = timeHex.slice(8, 12);
    const timeLow = (parseInt(timeHex.slice(12), 16) | 0x6000).toString(16).padStart(4, '0');
    
    const clockSeq = Math.floor(Math.random() * 16384) | 0x8000;
    const node = Array.from({length: 6}, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join('');
    
    return `${timeHigh}-${timeMid}-${timeLow}-${clockSeq.toString(16)}-${node}`;
  };

  const generateNil = (): string => {
    return '00000000-0000-0000-0000-000000000000';
  };

  const generateUuid = (version: string): string => {
    switch (version) {
      case 'v1': return generateV1();
      case 'v4': return generateV4();
      case 'v7': return generateV7();
      case 'v6': return generateV6();
      case 'nil': return generateNil();
      default: return generateV4();
    }
  };

  const formatUuid = (uuid: string, format: string): string => {
    const cleanUuid = uuid.replace(/-/g, '');
    
    switch (format) {
      case 'standard':
        return uuid.toLowerCase();
      case 'noDashes':
        return cleanUuid.toLowerCase();
      case 'uppercase':
        return uuid.toUpperCase();
      case 'lowercase':
        return uuid.toLowerCase();
      case 'braces':
        return `{${uuid.toLowerCase()}}`;
      case 'urn':
        return `urn:uuid:${uuid.toLowerCase()}`;
      default:
        return uuid.toLowerCase();
    }
  };

  const calculateUuidQuality = useCallback((version: string, quantity: number): UuidQuality => {
    if (version === 'nil') return 'basic';
    if (version === 'v4' && quantity === 1) return 'standard';
    if ((version === 'v1' || version === 'v6' || version === 'v7') && quantity <= 10) return 'professional';
    return 'enterprise';
  }, []);

  const generateUuids = useCallback(() => {
    const newUuids: string[] = [];
    
    for (let i = 0; i < settings.quantity; i++) {
      const rawUuid = generateUuid(settings.version);
      const formattedUuid = formatUuid(rawUuid, settings.format);
      newUuids.push(formattedUuid);
    }
    
    setUuids(newUuids);
    const quality = calculateUuidQuality(settings.version, settings.quantity);
    setUuidQuality(quality);
    
    // Update analytics
    setAnalytics(prev => {
      const newHistory = [...prev.generationHistory, {
        timestamp: new Date(),
        version: settings.version,
        format: settings.format,
        quantity: settings.quantity
      }].slice(-100);
      
      const totalGenerated = prev.totalGenerated + settings.quantity;
      const avgPerGeneration = Math.round((prev.averagePerGeneration * prev.generationHistory.length + settings.quantity) / (prev.generationHistory.length + 1) * 10) / 10;
      
      const versionDist = { ...prev.versionDistribution };
      versionDist[settings.version] += settings.quantity;
      
      const formatDist = { ...prev.formatDistribution };
      formatDist[settings.format] += settings.quantity;
      
      return {
        totalGenerated,
        averagePerGeneration: avgPerGeneration,
        mostCommonVersion: settings.version,
        versionDistribution: versionDist,
        formatDistribution: formatDist,
        generationHistory: newHistory
      };
    });
  }, [settings, calculateUuidQuality]);

  const copyToClipboard = async () => {
    try {
      const textToCopy = uuids.join('\n');
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy UUIDs');
    }
  };

  const downloadUuids = () => {
    const content = uuids.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uuids-${settings.version}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getQualityColor = (quality: UuidQuality): string => {
    switch (quality) {
      case 'basic': return 'bg-gray-500';
      case 'standard': return 'bg-blue-500';
      case 'professional': return 'bg-purple-500';
      case 'enterprise': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getQualityText = (quality: UuidQuality): string => {
    switch (quality) {
      case 'basic': return 'Basic';
      case 'standard': return 'Standard';
      case 'professional': return 'Professional';
      case 'enterprise': return 'Enterprise';
      default: return 'Unknown';
    }
  };

  const getVersionDescription = (version: string): string => {
    switch (version) {
      case 'v1': return 'Timestamp + MAC address based';
      case 'v4': return 'Random/pseudo-random';
      case 'v6': return 'Reordered timestamp based';
      case 'v7': return 'Unix timestamp based';
      case 'nil': return 'All zeros (null UUID)';
      default: return '';
    }
  };

  const getFormatDescription = (format: string): string => {
    switch (format) {
      case 'standard': return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';
      case 'noDashes': return 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx';
      case 'uppercase': return 'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX';
      case 'lowercase': return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';
      case 'braces': return '{xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx}';
      case 'urn': return 'urn:uuid:xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';
      default: return '';
    }
  };

  useEffect(() => {
    generateUuids();
  }, []);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-green-50 to-blue-100'}
    transition-all duration-300
  `;

  return (
    <>
      <Helmet>
        <title>UUID Generator - Generate Version 1, 4, 6, 7 UUIDs Online</title>
        <meta name="description" content="Generate UUIDs (Universally Unique Identifiers) in multiple versions (v1, v4, v6, v7) and formats. Bulk generation, timestamp-based, and random UUIDs for developers." />
        <meta name="keywords" content="UUID generator, GUID generator, unique identifier, UUID v1, UUID v4, UUID v6, UUID v7, developer tools, random UUID, timestamp UUID" />
        <meta name="author" content="UUID Generator Tool" />
        <meta property="og:title" content="UUID Generator - Generate UUIDs Online" />
        <meta property="og:description" content="Professional UUID generation tool supporting v1, v4, v6, v7 versions with bulk generation and multiple formats." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="UUID Generator - Professional UUID Generation" />
        <meta name="twitter:description" content="Generate UUIDs in multiple versions and formats. Perfect for developers, databases, and unique identifiers." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "UUID Generator",
            "description": "Generate UUIDs (Universally Unique Identifiers) in multiple versions and formats",
            "url": window.location.href,
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "UUID v1 Generation",
              "UUID v4 Generation", 
              "UUID v6 Generation",
              "UUID v7 Generation",
              "Bulk UUID Generation",
              "Multiple Output Formats",
              "Timestamp-based UUIDs"
            ]
          })}
        </script>
      </Helmet>

      <div className={containerClasses}>
        <div className={`${isDarkMode ? 'dark' : ''}`}>
          <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
            {/* Header */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="flex justify-between items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
                <div className="flex items-center space-x-3">
                  <Hash className="w-8 h-8 text-green-600 dark:text-green-400" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                      UUID Generator
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Generate universally unique identifiers (v1, v4, v6, v7)
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowAnalytics(!showAnalytics)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title="Analytics"
                  >
                    <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title="Settings"
                  >
                    <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  </button>
                  
                  <button
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title="Toggle Theme"
                  >
                    {isDarkMode ? (
                      <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>
                  
                  <button
                    onClick={toggleFullscreen}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  >
                    {isFullscreen ? (
                      <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    ) : (
                      <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main UUID Generator */}
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <div className="space-y-6">
                    {/* UUID Display */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Generated UUID{uuids.length > 1 ? 's' : ''}
                        </label>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {uuids.length} UUID{uuids.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="relative">
                        <textarea
                          value={showUuids ? uuids.join('\n') : uuids.map(() => '••••••••-••••-••••-••••-••••••••••••').join('\n')}
                          readOnly
                          rows={Math.min(uuids.length, 8)}
                          className="w-full p-4 pr-24 text-sm font-mono bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:text-white resize-none"
                        />
                        <div className="absolute right-2 top-2 flex flex-col space-y-1">
                          <button
                            onClick={() => setShowUuids(!showUuids)}
                            className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title={showUuids ? "Hide" : "Show"}
                          >
                            {showUuids ? (
                              <EyeOff className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            ) : (
                              <Eye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={copyToClipboard}
                            className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title="Copy All"
                          >
                            {copied ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            )}
                          </button>
                          {uuids.length > 1 && (
                            <button
                              onClick={downloadUuids}
                              className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                              title="Download as File"
                            >
                              <Download className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* UUID Info */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Version</div>
                        <div className="font-medium text-gray-800 dark:text-white">
                          UUID {settings.version.toUpperCase()}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {getVersionDescription(settings.version)}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Format</div>
                        <div className="font-medium text-gray-800 dark:text-white capitalize">
                          {settings.format}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {getFormatDescription(settings.format).slice(0, 20)}...
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Quality</div>
                        <div className="font-medium text-gray-800 dark:text-white">
                          {getQualityText(uuidQuality)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Production ready
                        </div>
                      </div>
                    </div>

                    {/* UUID Strength */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Quality Level: {getQualityText(uuidQuality)}
                        </span>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            RFC 4122 Compliant
                          </span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getQualityColor(uuidQuality)}`}
                          style={{
                            width: uuidQuality === 'basic' ? '25%' :
                                   uuidQuality === 'standard' ? '50%' :
                                   uuidQuality === 'professional' ? '75%' : '100%'
                          }}
                        />
                      </div>
                    </div>

                    {/* Generate Button */}
                    <button
                      onClick={generateUuids}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                    >
                      <RefreshCw className="w-5 h-5" />
                      <span>Generate New UUID{settings.quantity > 1 ? 's' : ''}</span>
                    </button>

                    {/* Info Notice */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800 dark:text-blue-200">
                          <strong>UUID Standards:</strong> All generated UUIDs follow RFC 4122 standards ensuring global uniqueness. 
                          {settings.version === 'v1' && ' Version 1 includes timestamp and MAC address.'}
                          {settings.version === 'v4' && ' Version 4 uses random/pseudo-random numbers.'}
                          {settings.version === 'v6' && ' Version 6 is field-compatible with v1 but with improved sorting.'}
                          {settings.version === 'v7' && ' Version 7 features improved entropy characteristics and time ordering.'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings Panel */}
              {showSettings && (
                <div className="lg:col-span-1">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center space-x-2">
                      <Settings className="w-5 h-5" />
                      <span>Configuration</span>
                    </h3>
                    
                    <div className="space-y-4">
                      {/* Version */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          UUID Version
                        </label>
                        <select
                          value={settings.version}
                          onChange={(e) => setSettings(prev => ({ ...prev, version: e.target.value as any }))}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                        >
                          <option value="v1">Version 1 (Timestamp + MAC)</option>
                          <option value="v4">Version 4 (Random)</option>
                          <option value="v6">Version 6 (Reordered Time)</option>
                          <option value="v7">Version 7 (Unix Timestamp)</option>
                          <option value="nil">Nil UUID (All Zeros)</option>
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {getVersionDescription(settings.version)}
                        </p>
                      </div>

                      {/* Format */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Output Format
                        </label>
                        <select
                          value={settings.format}
                          onChange={(e) => setSettings(prev => ({ ...prev, format: e.target.value as any }))}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                        >
                          <option value="standard">Standard (with dashes)</option>
                          <option value="noDashes">No Dashes</option>
                          <option value="uppercase">Uppercase</option>
                          <option value="lowercase">Lowercase</option>
                          <option value="braces">With Braces {}</option>
                          <option value="urn">URN Format</option>
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
                          {getFormatDescription(settings.format)}
                        </p>
                      </div>

                      {/* Quantity */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Quantity: {settings.quantity}
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="1000"
                          value={settings.quantity}
                          onChange={(e) => setSettings(prev => ({ ...prev, quantity: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        />
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <span>1</span>
                          <span>1000</span>
                        </div>
                      </div>

                      {/* Advanced Options for specific versions */}
                      {(settings.version === 'v1' || settings.version === 'v6' || settings.version === 'v7') && (
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center space-x-2">
                            <Clock className="w-4 h-4" />
                            <span>Timestamp Options</span>
                          </h4>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings.includeTimestamp}
                              onChange={(e) => setSettings(prev => ({ ...prev, includeTimestamp: e.target.checked }))}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Show Timestamp Info</span>
                          </label>
                        </div>
                      )}

                      {/* UUID Validation */}
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          UUID Validator
                        </h4>
                        <textarea
                          placeholder="Paste a UUID to validate..."
                          rows={2}
                          className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                        />
                        <button className="mt-2 w-full py-2 px-3 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors">
                          Validate UUID
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Analytics Panel */}
              {showAnalytics && (
                <div className="lg:col-span-1">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center space-x-2">
                      <BarChart3 className="w-5 h-5" />
                      <span>Analytics</span>
                    </h3>
                    
                    <div className="space-y-4">
                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {analytics.totalGenerated}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            Total Generated
                          </div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {analytics.averagePerGeneration}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            Avg Per Batch
                          </div>
                        </div>
                      </div>

                      {/* Version Distribution */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Version Usage
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(analytics.versionDistribution).map(([version, count]) => (
                            <div key={version} className="flex justify-between items-center text-sm">
                              <span className="text-gray-600 dark:text-gray-400">
                                UUID {version.toUpperCase()}
                              </span>
                              <span className="font-medium text-gray-800 dark:text-white">
                                {count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Format Distribution */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Format Usage
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(analytics.formatDistribution).map(([format, count]) => (
                            <div key={format} className="flex justify-between items-center text-sm">
                              <span className="text-gray-600 dark:text-gray-400 capitalize">
                                {format}
                              </span>
                              <span className="font-medium text-gray-800 dark:text-white">
                                {count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Recent History */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Recent Generations
                        </h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {analytics.generationHistory.slice(-5).reverse().map((entry, index) => (
                            <div key={index} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                              <span>{entry.version.toUpperCase()}</span>
                              <span>{entry.quantity}x</span>
                              <span className="capitalize">{entry.format}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UuidGenerator;